import {
  ModalProps,
  createUseModal,
  useFormModal,
} from "@imminently/react/context/modals";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import { Button, MenuItem, Select, SelectChangeEvent } from "@mui/material";
import { useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import MultilineTextInput from "@/components/MultilineTextInput";
import PatientCardModal from "@/components/PatientCardModal";
import { Patient, PatientEncounter } from "@/resources/types";
import { notificationService } from "@/services";
import * as yup from "yup";

const { useCreate } = notificationService;

export const checkIfWeCanSendMessageForPatient = (patient: Patient) => {
  //TODO Should be changed in the future to correctly check that the user has a mobile application and is registered using mobile
  const isPatientRegisteredHimself = !!patient?.phone;
  return isPatientRegisteredHimself;
};

type Template = { key: number; value: string, defaultValue?: string };

// Patient instruction message templates
const templates: Template[] = [
  // {
  //   key: 1,
  //   value: "Please wait in the waiting room. The doctor will see you shortly.",
  // },
  // {
  //   key: 2,
  //   value: "Please approach the front desk.",
  // },
  // {
  //   key: 3,
  //   value: "Please see the doctor in room 1.",
  // },
  {
    key: 4,
    value: "Please come to the triage desk.",
  },
  {
    key: 5,
    value: "Please return to My Emergency Visit (MEV) and complete the additional health questionnaires.",
  },
  {
    key: 6,
    value: "Some blood tests have been requested, please stay in the waiting room until called.",
  },
  {
    key: 7,
    value: "An imaging test (e.g., x-ray) has been requested, please stay in the waiting room until called.",
  },
  // --
  {
    key: 99,
    value: "Custom",
    defaultValue: "",
  },
];

interface PatientInstructionModalProps {
  encounter: PatientEncounter;
}

const PatientInstructionModal = ({ props }: ModalProps<PatientInstructionModalProps>) => {
  const { encounter } = props;

  const [template, setTemplate] = useState(templates[0].key);
  const { mutateAsync, isLoading } = useCreate();
  const { methods, notify, close } = useFormModal({
    mode: "all",
    defaultValues: {
      note: templates[0].value,
    },
    resolver: yupResolver(yup.object().shape({
      note: yup.string().required(),
    })),
    shouldUseNativeValidation: false,
  });

  const { control, handleSubmit, formState } = methods;
  const isAllowSendMessage = checkIfWeCanSendMessageForPatient(encounter.patient);

  const onChangeTemplate = (event: SelectChangeEvent<number>) => {
    const id = event.target.value as number;
    const temp = templates.find((t) => t.key === id);
    if (temp) {
      setTemplate(id);
      methods.setValue("note", temp.defaultValue ?? temp.value);
      methods.clearErrors("note");
    }
  };

  const submit = (data: any) => {
    const { note } = data;

    mutateAsync({
      encounterId: encounter.id,
      priority: "normal",
      template: "general",
      message: note,
    }).catch(() => {
      notify.error("Failed to send message");
    });

    close(true);
  };

  return (
    <PatientCardModal
      title="Send message to the following patient"
      encounter={encounter}
      onClose={() => close()}
    >
      <Select
        sx={{
          mb: 2,
          maxWidth: 500,
        }}
        fullWidth
        variant="outlined"
        label={null}
        value={template}
        onChange={onChangeTemplate}
      >
        {templates.map((t) => (
          <MenuItem key={t.key} value={t.key}>
            {t.value}
          </MenuItem>
        ))}
      </Select>

      <MultilineTextInput
        control={control}
        field="note"
        required={true}
        label="Message..."
        rows={6}
      />

      <Button
        sx={{ ml: "auto" }}
        disabled={isLoading || (formState.isDirty && !formState.isValid) || !isAllowSendMessage}
        color="success"
        startIcon={<SendOutlinedIcon />}
        onClick={handleSubmit(submit)}
      >
        Send
      </Button>
    </PatientCardModal>
  );
};

export const usePatientInstructionModal = createUseModal(
  PatientInstructionModal
);
